import React, { useState, useEffect } from "react";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Layout,
  Palette,
  Eye,
  Save,
  Globe,
  Smartphone,
  Monitor,
  Settings,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { TemplateSelector } from "@/components/landing-page/TemplateSelector";
import { DragDropEditor } from "@/components/landing-page/DragDropEditor";
import { PreviewMode } from "@/components/landing-page/PreviewMode";
import { PublishDialog } from "@/components/landing-page/PublishDialog";
import { SEOSettings } from "@/components/landing-page/SEOSettings";
import { LandingPageData } from "@/models/storeModel";
import {
  createLandingPage,
  updateLandingPage,
  getLandingPageByStoreId,
  generateUniqueSlug,
  LandingPage,
} from "@/services/landingPage/landingPageService";

interface LandingPageResponse {
  id: string;
  store_id: string;
  slug: string;
  title: string;
  meta_description?: string;
  meta_keywords?: string;
  og_image?: string;
  published: boolean;
  content: {
    sections: any[];
    theme_colors: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
  created_at: string;
  updated_at: string;
}

export default function LandingPageBuilder() {
  const { t } = useTranslation();
  const { selectedStoreId, stores } = useStoreManagementStore();
  const [currentStep, setCurrentStep] = useState<
    "template" | "edit" | "seo" | "preview"
  >("template");
  const [landingPageData, setLandingPageData] =
    useState<LandingPageData | null>(null);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">(
    "desktop"
  );
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [landingPageId, setLandingPageId] = useState<string | null>(null);
  const [hasExistingPage, setHasExistingPage] = useState(false);
  const [seoData, setSeoData] = useState({
    title: "",
    meta_description: "",
    meta_keywords: "",
    og_image: "",
  });

  const selectedStore = stores.find((store) => store.id === selectedStoreId);

  useEffect(() => {
    if (selectedStoreId) {
      loadExistingLandingPage();
    }
  }, [selectedStoreId]);

  const loadExistingLandingPage = async () => {
    if (!selectedStoreId) return;

    setIsLoading(true);
    try {
      const existingPage: any = await getLandingPageByStoreId(selectedStoreId);

      if (existingPage && existingPage.content) {
        const content = existingPage.content;
        setLandingPageId(existingPage.id);
        setHasExistingPage(true);
        setLandingPageData({
          id: existingPage.id,
          store_id: existingPage.store_id,
          title: existingPage.title,
          slug: existingPage.slug,
          template_type: "restaurant",
          sections: content.sections || [],
          published: existingPage.published,
          theme_colors: content.theme_colors || {
            primary: "#6366F1",
            secondary: "#EC4899",
            accent: "#8B5CF6",
          },
        });
        setSeoData({
          title: existingPage.title,
          meta_description: existingPage.meta_description || "",
          meta_keywords: existingPage.meta_keywords || "",
          og_image: existingPage.og_image || "",
        });
        setCurrentStep("edit");
      } else {
        setHasExistingPage(false);
        setCurrentStep("template");
      }
    } catch (error) {
      console.error("Error loading landing page:", error);
      setHasExistingPage(false);
      toast({
        title: t("common.error"),
        description: "Failed to load landing page",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template: LandingPageData) => {
    setLandingPageData(template);
    setSeoData({
      title: selectedStore?.name
        ? `${selectedStore.name} - Restaurant`
        : "Restaurant",
      meta_description: selectedStore?.name
        ? `Visit ${selectedStore.name} for an exceptional dining experience.`
        : "",
      meta_keywords: "restaurant, food, dining, cuisine",
      og_image: "",
    });
    setCurrentStep("edit");
  };

  const handleSave = async () => {
    if (!landingPageData || !selectedStoreId) return;

    setIsLoading(true);
    try {
      const saveData = {
        store_id: selectedStoreId,
        title: seoData.title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        og_image: seoData.og_image,
        content: {
          sections: landingPageData.sections,
          theme_colors: landingPageData.theme_colors,
        },
        published: landingPageData.published || false,
      };

      let result: any;
      if (landingPageId) {
        result = await updateLandingPage(landingPageId, saveData);
      } else {
        if (selectedStore) {
          const slug = (await generateUniqueSlug(selectedStore.name)) as string;
          result = await createLandingPage({ ...saveData, slug });
          setLandingPageId(result.id);
          setLandingPageData((prev) =>
            prev ? { ...prev, slug, id: result.id } : null
          );
        }
      }

      toast({
        title: t("common.success"),
        description: "Landing page saved successfully",
      });
    } catch (error) {
      console.error("Error saving landing page:", error);
      toast({
        title: t("common.error"),
        description: "Failed to save landing page",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async (slug: string) => {
    if (!landingPageData || !landingPageId) return;

    setIsLoading(true);
    try {
      await updateLandingPage(landingPageId, {
        slug,
        published: true,
        title: seoData.title,
        meta_description: seoData.meta_description,
        meta_keywords: seoData.meta_keywords,
        og_image: seoData.og_image,
        content: {
          sections: landingPageData.sections,
          theme_colors: landingPageData.theme_colors,
        },
      });

      setLandingPageData((prev) =>
        prev ? { ...prev, slug, published: true } : null
      );
      toast({
        title: t("common.success"),
        description: "Landing page published successfully",
      });
      setPublishDialogOpen(false);
    } catch (error) {
      console.error("Error publishing landing page:", error);
      toast({
        title: t("common.error"),
        description: "Failed to publish landing page",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSEOUpdate = (field: string, value: string) => {
    setSeoData((prev) => ({ ...prev, [field]: value }));
    if (field === "title" && landingPageData) {
      setLandingPageData((prev) => (prev ? { ...prev, title: value } : null));
    }
  };

  if (!selectedStoreId) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t("landing-page.title")}</h2>
          <p className="text-muted-foreground">
            {t("landing-page.selectStore")}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && currentStep === "template") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t("landing-page.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("landing-page.subtitle")}
              </p>
            </div>

            {landingPageData && (
              <div className="flex items-center gap-3">
                {landingPageData.published && (
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    <Globe className="h-3 w-3 mr-1" />
                    Published
                  </Badge>
                )}

                <div className="flex rounded-lg border p-1 bg-white shadow-sm">
                  <Button
                    variant={previewMode === "desktop" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setPreviewMode("desktop")}
                  >
                    <Monitor className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={previewMode === "mobile" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setPreviewMode("mobile")}
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                </div>

                <Button
                  variant="outline"
                  onClick={() => setCurrentStep("preview")}
                  disabled={!landingPageData}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>

                <Button
                  onClick={handleSave}
                  disabled={!landingPageData || isLoading}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>

                <Button
                  onClick={() => setPublishDialogOpen(true)}
                  disabled={!landingPageData}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Publish
                </Button>
              </div>
            )}
          </div>
        </div>

        <Tabs
          value={currentStep}
          onValueChange={(value) => setCurrentStep(value as any)}
        >
          <TabsList className="grid w-full grid-cols-4 bg-white shadow-sm">
            {!hasExistingPage && (
              <TabsTrigger value="template" className="flex items-center gap-2">
                <Layout className="h-4 w-4" />
                Template
              </TabsTrigger>
            )}
            <TabsTrigger
              value="edit"
              disabled={!landingPageData}
              className="flex items-center gap-2"
            >
              <Palette className="h-4 w-4" />
              Edit
            </TabsTrigger>
            <TabsTrigger
              value="seo"
              disabled={!landingPageData}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              SEO
            </TabsTrigger>
            <TabsTrigger
              value="preview"
              disabled={!landingPageData}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          {!hasExistingPage && (
            <TabsContent value="template" className="mt-6">
              <TemplateSelector onSelectTemplate={handleTemplateSelect} />
            </TabsContent>
          )}

          <TabsContent value="edit" className="mt-6">
            {landingPageData && (
              <DragDropEditor
                landingPageData={landingPageData}
                onUpdate={setLandingPageData}
                previewMode={previewMode}
              />
            )}
          </TabsContent>

          <TabsContent value="seo" className="mt-6">
            <div className="max-w-2xl">
              <SEOSettings
                title={seoData.title}
                metaDescription={seoData.meta_description}
                metaKeywords={seoData.meta_keywords}
                ogImage={seoData.og_image}
                onUpdate={handleSEOUpdate}
              />
            </div>
          </TabsContent>

          <TabsContent value="preview" className="mt-6">
            {landingPageData && (
              <PreviewMode
                landingPageData={landingPageData}
                previewMode={previewMode}
              />
            )}
          </TabsContent>
        </Tabs>

        <PublishDialog
          open={publishDialogOpen}
          onOpenChange={setPublishDialogOpen}
          onPublish={handlePublish}
          currentSlug={landingPageData?.slug}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
